import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { RootState } from 'reducers';
import { Modal, CheckboxTable } from 'components';
import { PlanService, UserService } from 'services';
import { Globals } from 'utils';
import { CheckmarkBadgeIcon, CloseIcon } from 'assets/icons';
import PaymentMethodsModal from './payment-methods-modal';
import DisclaimerModal from './disclaimer-modal';

const MODAL = {
	NONE: 0,
	VENDOR_CODE: 1,
	PAYMENT_METHODS: 2,
	DISCLAIMER: 3,
	CONFIRM_EXONERADO: 4,
}

interface State {
	modal: number
	availablePlans: any[]
	canPay: boolean
	payment_methods: any[]
	form: {
		frequency: string
		plan_id: string | number
		payment_method_id: string
		isOmitedVendor: boolean
		vendorCode: string
	}
}

class NewPlanCard extends React.Component<any, State> {

	state: State = {
		modal: MODAL.NONE,
		availablePlans: [],
		canPay: false,
		payment_methods: [],
		form: {
			frequency: '',
			plan_id: '',
			payment_method_id: '',
			isOmitedVendor: false,
			vendorCode: '',
		},
	}

	componentDidMount() {
		this.load();
	}

	load = async () => {
		const selects: any = await UserService.admin.getSelects();

		const getFinalAmounts = (base_price: number) => {
			const base = Number(base_price || 0)
			const iva_perc = Number(selects?.iva?.percentage || 0);
			const iva = base * (iva_perc / 100);
			const price = base + iva;
			const price_local = price * selects?.conversion;
			return { price, price_local };
		}

		const availablePlans = selects.plans
			.filter((i: any) => i.affiliate_type === this.props.user?.affiliate_type)
			.map((plan: any) => {
				let contentArray: string[] = [];
				if (Array.isArray(plan.formatted_content)) {
					contentArray = plan.formatted_content;
				} else if (Array.isArray(plan.raw?.formatted_content)) {
					contentArray = plan.raw.formatted_content;
				} else if (Array.isArray(plan.content)) {
					contentArray = plan.content;
				}

				return {
					...plan,
					content: contentArray,
					options: [
						{
							id: '1',
							title: 'Mensual',
							...getFinalAmounts(plan.mensual_amount),
						},
						{
							id: '2',
							title: 'Trimestral',
							...getFinalAmounts(plan.trimestral_amount),
						},
						{
							id: '3',
							title: 'Semestral',
							...getFinalAmounts(plan.semestral_amount),
						},
						{
							id: '4',
							title: 'Anual',
							...getFinalAmounts(plan.amount),
						},
					]
				};
			});

		const nextPayment = moment(this.props.user?.last_payment?.next_payment).subtract(5, 'days');

		this.setState(s => ({
			availablePlans,
			canPay: moment().isSameOrAfter(nextPayment),
			payment_methods: selects.paymentMethods.filter((i: any) => i.id !== 3).sort((a: any, b: any) => String(a.name).localeCompare(String(b.name))),
			form: {
				...s.form,
				plan_id: availablePlans.length > 0 ? availablePlans[0].id : '',
			}
		}));
	}
	change = async (value: any, target: keyof State['form'], callback?: () => void) => {
		this.setState({ form: { ...this.state.form, [target]: value } }, callback);
	}

	onCloseModal = async (modal: number, value?: any) => {
		if (modal === MODAL.VENDOR_CODE) {
			this.change(value || '', 'vendorCode');
			this.setState({ modal: MODAL.NONE });

		} else if (modal === MODAL.PAYMENT_METHODS) {
			await this.change(value || '', 'payment_method_id');
			if (value) {
				const selectedMethod: any = this.state.payment_methods.find((i: any) => i.id === +value);
				const isExonerado = selectedMethod?.name.toLowerCase().includes('exonerado');

				if (isExonerado) {
					this.setState({ modal: MODAL.CONFIRM_EXONERADO });
				} else {
					this.setState({ modal: MODAL.NONE });
					this.onSubmit();
				}
			} else {
				this.setState({ modal: MODAL.NONE });
			}
		} else {
			this.setState({ modal: MODAL.NONE });
		}
	}

	onSubmit = async () => {
		const currentSelectedPlan = this.state.availablePlans.find((p: any) => p.id === this.state.form.plan_id);

		if (!currentSelectedPlan)
			return Globals.showError('Debe seleccionar un plan');

		const { form } = this.state;

		if (!form.frequency)
			return Globals.showError('Debe seleccionar una frecuencia de pago');

		if (!form.isOmitedVendor && !form.vendorCode)
			return this.setState({ modal: MODAL.DISCLAIMER });

		if (!form.payment_method_id)
			return this.setState({ modal: MODAL.PAYMENT_METHODS });

		this.props.dispatch({ type: 'SET_REQUEST_PLAN', payload: form });
		this.props.history.push('/affiliate/renew');
	}

	submitExonerado = async () => {
		Globals.setLoading();
		try {
			const { form, availablePlans } = this.state;
			const currentSelectedPlan = availablePlans.find((p: any) => p.id === form.plan_id);
			const selects: any = await UserService.admin.getSelects();

			let base_price = 0;
			switch (Number(form.frequency)) {
				case 1: base_price = Number(currentSelectedPlan?.mensual_amount); break;
				case 2: base_price = Number(currentSelectedPlan?.trimestral_amount); break;
				case 3: base_price = Number(currentSelectedPlan?.semestral_amount); break;
				case 4: base_price = Number(currentSelectedPlan?.amount); break;
			}

			const iva_percentage = Number(selects?.iva?.percentage || 0);
			const iva = (base_price * iva_percentage) / 100;
			const amount = base_price + iva;
			const amount_bs = Number(parseFloat(String(amount * selects?.conversion)).toFixed(2));
			const todayStr = new Date().toISOString().split('T')[0];

			const payload = {
				plan_id: Number(currentSelectedPlan?.id),
				user_id: this.props.user?.id,
				frequency: Number(form.frequency),
				payment_method_id: form.payment_method_id,
				currency: 2,
				seller_code: form.vendorCode || null,
				subtotal: base_price,
				iva: iva,
				iva_percentage: iva_percentage,
				amount: amount,
				amount_bs: amount_bs,
				conversion: selects?.conversion || 1,

				payment_document: String(this.props.user?.document || '0'),
				payment_date: todayStr,
				payment_name: String(this.props.user?.name || 'Exonerado'),
				payment_email: String(this.props.user?.email || 'exonerado@izoo.com'),
				reference: 'EXONERACION',
			};

			await PlanService.affiliate.payPlan(payload);

			Globals.quitLoading();
			this.setState({ modal: MODAL.NONE });
			Globals.showSuccess('Su solicitud de exoneración ha sido registrada con éxito. Espere su aprobación');

			window.location.reload();

		} catch (error) {
			Globals.quitLoading();
			console.log(error);
			Globals.showError('Ocurrió un error al procesar la solicitud');
		}
	}

	render() {
		const { availablePlans, form } = this.state;

		const currentSelectedPlan = availablePlans.find((p: any) => p.id === form.plan_id);

		const hasPendingPayment = this.props.hasPendingPayment;
		const isPaidButUnverified = this.props.user?.verification === 0 && this.props.user?.last_payment?.status === 1;
		const isButtonDisabled = !this.state.canPay || hasPendingPayment || isPaidButUnverified;
		return (
			<>
				<DisclaimerModal
					visible={this.state.modal === MODAL.DISCLAIMER}
					onClose={() => {
						this.onCloseModal(MODAL.DISCLAIMER);
						this.setState({ modal: MODAL.VENDOR_CODE });
					}}
				/>

				<Modal
					className="modal-vendor-code"
					withoutClose
					visible={this.state.modal === MODAL.VENDOR_CODE}
					onClose={() => this.onCloseModal(MODAL.VENDOR_CODE)}
				>
					<VendorCode
						onClose={() => this.onCloseModal(MODAL.VENDOR_CODE)}
						onOmit={async () => {
							await this.change(true, 'isOmitedVendor', () => this.onCloseModal(MODAL.VENDOR_CODE));
							this.setState({ modal: MODAL.PAYMENT_METHODS });
						}}
						onSuccess={value => {
							this.onCloseModal(MODAL.VENDOR_CODE, value);
							this.setState({ modal: MODAL.PAYMENT_METHODS });
						}}
					/>
				</Modal>

				<PaymentMethodsModal
					visible={this.state.modal === MODAL.PAYMENT_METHODS}
					onClose={methodId => {
						this.onCloseModal(MODAL.PAYMENT_METHODS, methodId);
					}}
					paymentMethods={this.state.payment_methods}
					isMembership={true}
				/>

				<Modal
					className="modal-vendor-code"
					withoutClose
					visible={this.state.modal === MODAL.CONFIRM_EXONERADO}
					onClose={() => this.setState({ modal: MODAL.NONE })}
				>
					<div className="card" style={{ padding: '45px 30px' }}>

						<p className="title" style={{ fontSize: '22px', marginBottom: '10px' }}>
							Confirmar solicitud
						</p>
						<p className="subtitle" style={{ marginBottom: '35px', padding: '0 15px', lineHeight: '1.5' }}>
							Está a punto de solicitar la exoneración de su pago. ¿Desea continuar?
						</p>

						<div className="actions" style={{ display: 'flex', gap: '15px', padding: 0 }}>
							<button
								className="alt"
								style={{ flex: 1, margin: 0, padding: '12px 0' }}
								onClick={() => this.setState({ modal: MODAL.PAYMENT_METHODS })}
							>
								CANCELAR
							</button>
							<button
								style={{ flex: 1, margin: 0, padding: '12px 0' }}
								onClick={this.submitExonerado}
							>
								REALIZAR SOLICITUD
							</button>
						</div>
					</div>
				</Modal>

				<aside className="affiliate-plan__sidebar">
					{!!currentSelectedPlan && (
						<div className="plan-card">
							<div className="plan-card__header">
								<img className="poster" src={Globals.fromPhotos(currentSelectedPlan?.icon)} alt="Plan Poster" />
								<div className="plan-card__main-info">
									<span className="plan-card__label">Afiliación</span>
									<div className="plan-card__details">
										<h2 className="plan-card__name">{currentSelectedPlan?.name}</h2>
										{!!form.frequency && (
											<span className="plan-card__price">{Globals.formatMiles(currentSelectedPlan?.options?.find((i: any) => i.id === form.frequency)?.price, true)}</span>
										)}
									</div>
								</div>
							</div>

							<div className="plan-card__body">
								{availablePlans.length > 1 && (
									<>
										<h3 className="plan-card__payment-title" style={{ marginTop: 0 }}>Seleccione su plan</h3>
										<div className="payment-options" style={{ marginBottom: '20px' }}>
											{availablePlans.map((plan: any, index: number) => {
												const isActive = plan.id === form.plan_id;
												return (
													<div
														key={`plan-${index}`}
														className="option"
														data-active={String(Boolean(isActive))}
														onClick={() => this.change(plan.id, 'plan_id')}
													>
														<CheckboxTable active={isActive} />
														<div className="option__content">
															<span className="option__label">{plan.name}</span>
														</div>
													</div>
												)
											})}
										</div>
									</>
								)}

								<p className="plan-card__subtitle">Contenido</p>
								<ul className="plan-card__features">
									{Array.isArray(currentSelectedPlan?.content) && currentSelectedPlan.content.length > 0 ? (
										currentSelectedPlan.content.map((featureItem: string, index: number) => (
											<li key={`feature-${index}`}>
												<img src={CheckmarkBadgeIcon} alt="Checkmark Badge" />
												<span>{featureItem}</span>
											</li>
										))
									) : (
										<li>
											<img src={CheckmarkBadgeIcon} alt="Checkmark Badge" />
											<span>Beneficios estándar incluidos</span>
										</li>
									)}
								</ul>

								<h3 className="plan-card__payment-title">¿Cómo desea pagar su cuota?</h3>

								<div className="payment-options">
									{currentSelectedPlan?.options?.map((item: any, index: number) => {
										const isActive = item.id === this.state.form.frequency;
										return (
											<div
												key={`freq-${index}`}
												className="option"
												data-active={String(Boolean(isActive))}
												onClick={() => this.change(item.id, 'frequency')}
											>
												<CheckboxTable active={isActive} />
												<div className="option__content">
													<span className="option__label">{item.title}</span>
													<div className="option__price">
														<strong>{Globals.formatMiles(item.price, true)}</strong>
														<small>{Globals.formatMiles(item.price_local, true, 'Bs.')}</small>
													</div>
												</div>
											</div>
										)
									})}
								</div>

								<button className="btn-pay" onClick={this.onSubmit} disabled={isButtonDisabled}>
									PAGAR
								</button>
							</div>
						</div>
					)}

					{(!availablePlans || availablePlans.length === 0) && (
						<div className="no-plan">
							<p>No hay un plan disponible actualmente</p>
						</div>
					)}
				</aside>
			</>
		)
	}
}

interface VendorCodeProps {
	onClose: () => void
	onOmit: () => void
	onSuccess: (code: string) => void
}

const VendorCode = ({ onClose, onOmit, onSuccess }: VendorCodeProps) => {
	const vendorCodeLength = 6;
	const inputRefs = useRef<Array<any>>([]);

	const [vendorCode, setVendorCode] = useState<string[]>(
		Array.from({ length: vendorCodeLength }, () => '')
	)

	const canSubmitVendorCode = useMemo(
		() => vendorCode.every(value => value.trim().length > 0),
		[vendorCode]
	)
	const handleVendorChange = (index: number, value: string) => {
		const nextValue = value.slice(-1);
		setVendorCode(current => {
			const next = [...current];
			next[index] = nextValue;
			return next;
		});

		if (nextValue && index < vendorCodeLength - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	}

	const handleVendorBackspace = (index: number, key: string) => {
		if (key !== 'Backspace') return;
		if (vendorCode[index]) return;
		if (index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	}

	const handleSubmit = async () => {
		if (!canSubmitVendorCode) return;

		try {
			const code = vendorCode.join('');
			const res = await UserService.admin.validateVendorCode({ code });

			if (!res) return Globals.showError('Código de vendedor no válido');

			onSuccess(code);

		} catch (error) {
			console.log(error);
		}
	}

	return (
		<div className="card">
			<button className="close" onClick={onClose}>
				<img src={CloseIcon} alt="Close" />
			</button>
			<p className="title">Código del Vendedor</p>
			<p className="subtitle">
				Favor Introduzca el código de referido enviado por su vendedor
			</p>
			<div className="code-row">
				{vendorCode.map((value, index) => (
					<input
						key={`code-${index}`}
						ref={ref => {
							inputRefs.current[index] = ref;
						}}
						value={value}
						onChange={e => handleVendorChange(index, e?.target?.value)}
						onKeyDown={({ nativeEvent }) => handleVendorBackspace(index, nativeEvent.key)}
						maxLength={1}
					/>
				))}
			</div>
			<div className="actions">
				<button className="alt" onClick={onOmit}>OMITIR</button>
				<button disabled={!canSubmitVendorCode} onClick={handleSubmit}>
					GUARDAR
				</button>
			</div>
		</div>
	)
}

export default connect((state: RootState) => {
	return {
		user: state.user
	}
})(NewPlanCard);